import { ArrowLeftSmallIcon, ArrowRightSmallIcon } from '@blocksuite/icons/rc';
import { Button } from '@pique/component';
import {
  SettingHeader,
  SettingRow,
  SettingWrapper,
} from '@pique/component/setting-components';
import { openSettingModalAtom } from '@pique/core/atoms';
import { useWorkspace } from '@pique/core/hooks/use-workspace';
import { useWorkspaceInfo } from '@pique/core/hooks/use-workspace-info';
import { UNTITLED_WORKSPACE_NAME } from '@pique/env/constant';
import { useI18n } from '@pique/i18n';
import { FrameworkScope } from '@toeverything/infra';
import { useSetAtom } from 'jotai';
import { useCallback } from 'react';

import { DeleteLeaveWorkspace } from './delete-leave-workspace';
import { EnableCloudPanel } from './enable-cloud';
import { LabelsPanel } from './labels';
import { MembersPanel } from './members';
import { ProfilePanel } from './profile';
import { backButton } from './styles.css';
import type { WorkspaceSettingDetailProps } from './types';

export const WorkspaceSettingDetail = ({
  workspaceMetadata,
}: WorkspaceSettingDetailProps) => {
  const t = useI18n();
  const setOpenSettingModalAtom = useSetAtom(openSettingModalAtom);

  // useWorkspace hook is a vary heavy operation here, but we need syncing name and avatar changes here,
  // we don't have a better way to do this now
  const workspace = useWorkspace(workspaceMetadata);

  const workspaceInfo = useWorkspaceInfo(workspaceMetadata);

  const handleBackToWorkspaces = useCallback(() => {
    setOpenSettingModalAtom(prev => ({
      ...prev,
      activeTab: 'workspaces',
      workspaceMetadata: null,
    }));
  }, [setOpenSettingModalAtom]);

  const handleResetSyncStatus = useCallback(() => {
    workspace?.engine.doc
      .resetSyncStatus()
      .then(() => {
        window.location.reload();
      })
      .catch((err: unknown) => {
        console.error(err);
      });
  }, [workspace]);

  if (!workspace) {
    return null;
  }

  return (
    <FrameworkScope scope={workspace.scope}>
      <Button
        variant="plain"
        className={backButton}
        prefix={<ArrowLeftSmallIcon />}
        onClick={handleBackToWorkspaces}
      >
        Back to Workspaces
      </Button>
      <SettingHeader
        title={t[`Workspace Settings with name`]({
          name: workspaceInfo?.name ?? UNTITLED_WORKSPACE_NAME,
        })}
        subtitle={t['com.pique.settings.workspace.description']()}
      />
      <SettingWrapper title={t['Info']()}>
        <SettingRow
          name={t['Workspace Profile']()}
          desc={t['com.pique.settings.workspace.not-owner']()}
          spreadCol={false}
        >
          <ProfilePanel />
          <LabelsPanel />
        </SettingRow>
      </SettingWrapper>
      <SettingWrapper title={t['com.pique.brand.affineCloud']()}>
        <EnableCloudPanel />
        <MembersPanel />
      </SettingWrapper>
      <SettingWrapper>
        <DeleteLeaveWorkspace />
        <SettingRow
          name={
            <span style={{ color: 'var(--affine-text-secondary-color)' }}>
              {t['com.pique.resetSyncStatus.button']()}
            </span>
          }
          desc={t['com.pique.resetSyncStatus.description']()}
          style={{ cursor: 'pointer' }}
          onClick={handleResetSyncStatus}
          data-testid="reset-sync-status"
        >
          <ArrowRightSmallIcon />
        </SettingRow>
      </SettingWrapper>
    </FrameworkScope>
  );
};
